import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import {
  handleEditOperatorProfile,
  handleFetchOperatorReports,
  handleFetchOperators,
  handleFetchOperatorsBetweenDates,
  handleInviteOperator,
  handleRemoveOperator,
  handleUploadAvatar,
  handleFetchSortedOperatorsList,
  handleEditOperatorMobile,
} from './operators.helpers';
import { operatorTypes } from './operators.types';
import { toast } from 'react-toastify';
import {
  fetchOperatorsStart,
  saveToEditedOperator,
  setEditedOperator,
  setOperators,
  updateRemovedOperator,
  setOperatorReports,
  loadingOperators,
  setOperatorsTableData,
  setWeeklyChosenOperator,
  loadingWeeklyChosenOperatorStart,
  setSortedOperatorsList,
  loadingSortedOperatorsList,
} from './operators.actions';
import {
  createOperatorObject,
  createOperatorReportObject,
  createOperatorsObject,
  createOperatorsTableData,
  getOperatorRemovedNotif,
  getOperatosList,
  getScoringMetrics,
} from './operators.adapter';
import { sendVerificationSms } from '../user/user.sagas';
import { setMobileNumber } from '../app/app.actions';
import {
  setIsMobileValidated,
  setVerificationCodeTimerStatus,
} from '../app/app.actions';
import {
  setCurrentUser,
  validateSmsVerificationCodeStart,
} from '../user/user.actions';
import { API_URL } from '../../utils/constants';
import { getNotifMsg, handleErrors } from '../../utils';
import { getCalenderLastDays, convertDateToEnglish } from '../../utils/date';

const mapState = ({ appData }) => ({
  locale: appData.locale,
});

export function* inviteOperator({ payload }) {
  const { handleToggleModal } = payload;
  const { locale } = yield select(mapState);

  try {
    const returnedData = yield handleInviteOperator(payload);
    if (returnedData.error === null) {
      toast.success(getNotifMsg('NEW_OPERATOR_SUCCESS_INVITE', locale));
      handleToggleModal();
      yield put(fetchOperatorsStart());
    }
  } catch (error) {
    const returnedError = error.response.data.message;

    toast.error(returnedError);
    handleErrors(error, locale);
  }
}

export function* onInviteOperatorStart() {
  yield takeLatest(operatorTypes.INVITE_OPERATORS_START, inviteOperator);
}

const getWeeklyChoosenOperator = (operators) => {
  let mostAcceptedOperator;
  let mostAcceptedChats = 0;
  operators.forEach((operator) => {
    if (
      operator.statistic &&
      operator.statistic.totalAccepted > mostAcceptedChats
    ) {
      mostAcceptedOperator = operator;
      mostAcceptedChats = operator.statistic.totalAccepted;
    }
  });

  return mostAcceptedOperator;
};

export function* fetchWeeklyChosen() {
  yield put(loadingWeeklyChosenOperatorStart());
  let firstWeekDay = convertDateToEnglish(
    getCalenderLastDays(6, 'days')[0],
    '-'
  );
  let lastWeekDay = convertDateToEnglish(
    getCalenderLastDays(6, 'days')[1],
    '-'
  );
  let lastWeekOperatorReports = yield handleFetchOperatorsBetweenDates(
    firstWeekDay,
    lastWeekDay
  );
  let operators = createOperatorsObject(lastWeekOperatorReports);
  let weeklyChoosenOperator = getWeeklyChoosenOperator(lastWeekOperatorReports);
  if (weeklyChoosenOperator) {
    let weeklyChoosenOperatorReports = yield handleFetchOperatorReports(
      weeklyChoosenOperator.id,
      firstWeekDay,
      lastWeekDay
    );
    const adaptedChosenOperator = operators.filter(
      (item) => item.id === weeklyChoosenOperator.id
    )[0];
    let operatorObj = {
      ...adaptedChosenOperator,
      acceptedConversations: createOperatorReportObject(
        weeklyChoosenOperatorReports
      ).conversationsChartData.acceptedConversations,
    };
    yield put(setWeeklyChosenOperator(operatorObj));
  } else {
    yield put(setWeeklyChosenOperator(null));
  }
}

export function* onFetchWeeklyChosenOperatorStart() {
  yield takeLatest(
    operatorTypes.FETCH_WEEKLY_CHOSEN_OPERATOR,
    fetchWeeklyChosen
  );
}

export function* fetchOperators() {
  const { locale } = yield select(mapState);
  try {
    yield put(loadingOperators());
    const allOperators = yield handleFetchOperators();
    let operators = createOperatorsObject(allOperators);
    yield put(setOperators(operators));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onFetchOperatorsStart() {
  yield takeLatest(operatorTypes.FETCH_OPERATORS_START, fetchOperators);
}

export function* editOperatorProfile({ payload }) {
  const { locale } = yield select(mapState);

  const {
    values,
    selectedAccessLevel,
    selectedWebsiteIds,
    operatorId,
    handleToggleModal,
    uploadedImage,
    currentImageUrl,
    isAccountEqOperator,
    isMobileValidated,
    isCreator,
    accepted,
    workTime,
  } = payload;

  const { mobile, password } = values;

  try {
    let returnedUploadedPath = null;

    // upload avatar if it has changed
    if (uploadedImage) {
      returnedUploadedPath = yield handleUploadAvatar(
        uploadedImage,
        operatorId
      );

      // upload failed
      if (!returnedUploadedPath.success) {
        toast.error(getNotifMsg('UPLOAD_IMAGE_NOT_SUCCESSFUL', locale));
        return;
      }
    }

    if (!password || password === '') {
      delete values.password;
    }

    const imageUrlForApi = uploadedImage
      ? returnedUploadedPath.filePath
      : currentImageUrl.replace(API_URL, '');

    const imageUrlForUserObject = uploadedImage
      ? URL.createObjectURL(uploadedImage)
      : currentImageUrl;

    // save operator's data without validating mobile number by sending SMS if mobile number is the same as already saved number or on-edit operator is the same as account owner
    // comment-MH: As I was checking this flow seemed strange to me. check with product manager for it's validation
    if (!isAccountEqOperator || (isAccountEqOperator && isMobileValidated)) {
      const returnedData = yield handleEditOperatorProfile({
        operatorId,
        values,
        selectedAccessLevel,
        selectedWebsiteIds,
        imageUrl: imageUrlForApi,
      });

      if (!returnedData.error) {
        toast.success(getNotifMsg('OPERATOR_CHANGES_SUCCESSFUL', locale));
        handleToggleModal();
        yield put(
          setEditedOperator(
            createOperatorObject({
              values,
              selectedAccessLevel,
              operatorId,
              selectedWebsiteIds,
              imageUrl: imageUrlForUserObject,
              isCreator,
              accepted,
              workTime,
            })
          )
        );

        yield put(setCurrentUser({ mobile }));
      }

      if (isAccountEqOperator) {
        yield put(
          setCurrentUser({
            image: imageUrlForUserObject,
            fullName: values.fullName,
          })
        );
      }

      return;
    }

    // now sending SMS for mobile validation procedure
    const isSendSms = yield sendVerificationSms(mobile);
    if (isSendSms) {
      yield put(
        saveToEditedOperator({
          values,
          selectedAccessLevel,
          operatorId,
          selectedWebsiteIds,
          imageUrl: imageUrlForUserObject,
          isCreator,
          accepted,
          workTime,
        })
      );

      handleToggleModal();
      yield put(setMobileNumber(mobile));
      yield put(setIsMobileValidated(false));
      yield put(setVerificationCodeTimerStatus(true));
    }
  } catch (error) {
    if (error.response.status === 406) {
      toast.info(getNotifMsg('MOBILE_REGISTERED_BEFORE', locale));
    }

    handleErrors(error, locale);
  }
}

export function* onEditOperatorProfileStart() {
  yield takeLatest(
    operatorTypes.EDIT_OPERATOR_PROFILE_START,
    editOperatorProfile
  );
}

export function* editOperator({ payload }) {
  const {
    mobile,
    values,
    selectedAccessLevel,
    operatorId,
    selectedWebsiteIds,
    imageUrl,
    isCreator,
    workTime,
    accepted,
    toggle,
  } = payload;

  const { locale } = yield select(mapState);

  try {
    const returnedData = yield call(handleEditOperatorProfile, {
      values,
      selectedAccessLevel,
      operatorId,
      selectedWebsiteIds,
      imageUrl,
    });

    if (!returnedData.error) {
      toast.success(getNotifMsg('OPERATOR_CHANGES_SUCCESSFUL', locale));
      yield put(setIsMobileValidated(true));

      toggle && toggle();

      yield put(
        setEditedOperator(
          createOperatorObject({
            values,
            selectedAccessLevel,
            operatorId,
            selectedWebsiteIds,
            imageUrl,
            isCreator,
            accepted,
            workTime,
          })
        )
      );

      yield put(setCurrentUser({ mobile }));
      yield put(setVerificationCodeTimerStatus(false));
    }
  } catch (error) {
    if (error.response?.status === 406) {
      toast.info(getNotifMsg('MOBILE_REGISTERED_BEFORE', locale));
    }

    handleErrors(error, locale);
  }
}

export function* editOperatorStart() {
  yield takeLatest(operatorTypes.EDIT_OPERATOR_START, editOperator);
}

export function* removeOperator({ payload }) {
  const { id, fullName } = payload;
  const { locale } = yield select(mapState);

  try {
    const returnedData = yield handleRemoveOperator(id);
    if (returnedData) {
      toast.success(getOperatorRemovedNotif(fullName, locale));
      yield put(updateRemovedOperator(id));
    }
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onRemoveOperatorStart() {
  yield takeLatest(operatorTypes.REMOVE_OPERATOR_START, removeOperator);
}

export function* fetchOperatorReports({ payload }) {
  const { operator_id, startDate, endDate } = payload;
  const { locale } = yield select(mapState);

  try {
    const returnedData = yield handleFetchOperatorReports(
      operator_id,
      startDate,
      endDate
    );
    if (returnedData) {
      yield put(setOperatorReports(createOperatorReportObject(returnedData)));
    }
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onFetchOperatorReportsStart() {
  yield takeLatest(
    operatorTypes.FETCH_OPERATOR_REPORTS_START,
    fetchOperatorReports
  );
}

export function* fetchOperatorsBetweenDays({ payload }) {
  const { start_date, end_date } = payload;
  const { locale } = yield select(mapState);

  try {
    const returnedData = yield handleFetchOperatorsBetweenDates(
      start_date,
      end_date
    );
    if (returnedData) {
      yield put(setOperatorsTableData(createOperatorsTableData(returnedData)));
      return returnedData;
    }
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onFetchOperatorsBetweenDaysStart() {
  yield takeLatest(
    operatorTypes.FETCH_OPERATORS_BETWEEN_DAYS_START,
    fetchOperatorsBetweenDays
  );
}

export function* fetchSortedOperatorsList({ payload }) {
  const { start_date, end_date } = payload;
  yield put(loadingSortedOperatorsList(true));
  const { locale } = yield select(mapState);

  try {
    const res = yield handleFetchSortedOperatorsList(start_date, end_date);
    const sortedList = getOperatosList(res);
    const metrics = getScoringMetrics(res);
    yield put(setSortedOperatorsList(sortedList, metrics));
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    yield put(loadingSortedOperatorsList(false));
  }
}

export function* onFetchSortedOperatorsList() {
  yield takeLatest(
    operatorTypes.FETCH_SORTED_OPERATORS_LIST_START,
    fetchSortedOperatorsList
  );
}

function* changeOperatorMobileNumber({ payload }) {
  const { locale } = yield select(mapState);

  try {
    const { mobile, operatorId, onSuccess } = payload;

    const res = yield call(handleEditOperatorMobile, { mobile, operatorId });
    if (!res.error) {
      onSuccess && onSuccess();
    }
  } catch (error) {
    if (error.response?.status === 406) {
      toast.info(getNotifMsg('MOBILE_REGISTERED_BEFORE', locale));
      return;
    }

    handleErrors(error, locale);
  }
}

function* onChangeOperatorMobileNumber() {
  yield takeLatest(
    operatorTypes.CHANGE_OPERATOR_MOBILE_NUMBER,
    changeOperatorMobileNumber
  );
}

export default function* operatorSagas() {
  yield all([
    call(onInviteOperatorStart),
    call(onFetchOperatorsStart),
    call(onEditOperatorProfileStart),
    call(onRemoveOperatorStart),
    call(editOperatorStart),
    call(onFetchOperatorReportsStart),
    call(onFetchOperatorsBetweenDaysStart),
    call(onFetchWeeklyChosenOperatorStart),
    call(onFetchSortedOperatorsList),
    call(onChangeOperatorMobileNumber),
  ]);
}
